import { useQuery } from '@tanstack/react-query';
import { useApi } from './useApi';

/**
 * Fetch a shared JSON blob from /api/blob/{key}.
 *
 * The /api/blob endpoint returns {key, value, updatedAt, updatedBy} but the
 * caller almost always just wants `value` — so we unwrap it here.
 *
 * Returns the React Query result with `data` set to the unwrapped value
 * (so existing pages that expected `data` from the static JSON keep working
 * with a 1-line change).
 *
 * Used by the Excel-backed migrations (Phase 6+) to replace static
 * `import data from '/data/X.json'` or `fetch('/data/X.json')` patterns.
 */
export function useBlob(key, options = {}) {
  const apiFetch = useApi();
  const { staleTime = 5 * 60 * 1000, ...rest } = options;

  return useQuery({
    queryKey: ['blob', key],
    queryFn: async () => {
      const res = await apiFetch(`/blob/${key}`);
      if (res.status === 404) return null; // blob not yet uploaded
      if (!res.ok) throw new Error(`Failed to load blob '${key}': ${res.status}`);
      const body = await res.json();
      return body.value;
    },
    staleTime, // shared blobs change rarely; cache aggressively
    ...rest,
  });
}
